/**
 * Floor planner — the ceiling of the selected room(s).
 *
 * Plain enough for anyone: pick a SHAPE from three pictures (flat, sloped,
 * peaked), then shape it on a drawing of the room's cross-section by dragging
 * the high point — up and down for how steep, left and right to put the peak
 * off centre (or, for a sloped ceiling, to choose which wall is the high one).
 * The drag snaps to the standard roof pitches, and everything reads back in
 * plain words ("Peak 14′ · 6/12, moderate"); the builder's numbers (pitch,
 * rise, peak height) are there to type into as well.
 *
 * The wall height stays what it is — the ceiling rises above it. Works on one
 * room or several at once (they all take the same shape).
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CeilingProfile, RidgeAxis, STANDARD_PITCHES, ceilingGeometry, pitchForRise, pitchWord, riseForPitch } from '../../../calculations/ceiling/profile';
import { PlanRoom } from '../../../calculations/plan';
import { DimensionInput } from '../../ui/DimensionInput';
import { formatFeetInches } from '../SectionEditOverlay';
import { DESIGNER_STRINGS as S } from '../strings';

const C = S.planner.ceilingProfile;
const ACCENT = '#FEB100';
const INK = '#eef1f6';
const SOFT = 'rgba(238,241,246,0.45)';
const HALF_INCH_FT = 1 / 24;

export type CeilingShape = 'flat' | 'shed' | 'gable' | 'vaulted';

export interface CeilingEditorProps {
  /** The room shown (the first selected); `count` says how many take the change. */
  room: PlanRoom;
  count: number;
  maxHeight: number;
  onChange: (profile: CeilingProfile) => void;
  /** The plan's other rooms, to copy this ceiling to (one room selected only). */
  others?: { id: string; name: string }[];
  onCopyTo?: (ids: string[]) => void;
  /**
   * `wizard`: the create wizard's step — bigger shapes with a plain word each,
   * the drawing beside one-click steepness, the builder's numbers folded away.
   * `panel` (default): the room panel's narrow column.
   */
  variant?: 'panel' | 'wizard';
}

/** The steepness offered as one click in the wizard. */
const STEEPNESS = [3, 6, 9, 12] as const;

const shapeOf = (p: CeilingProfile | undefined): CeilingShape => (p?.kind ?? 'flat');

/** The span the slope runs across, and the room's side along the ridge. */
function spans(room: PlanRoom, axis: RidgeAxis): { across: number; along: number } {
  return axis === 'length' ? { across: room.width, along: room.length } : { across: room.length, along: room.width };
}

/** A sensible first profile when a shape is picked: a 6/12 pitch, capped at the height limit. */
function startingProfile(room: PlanRoom, shape: CeilingShape, previous: CeilingProfile | undefined, maxHeight: number): CeilingProfile {
  if (shape === 'flat') return { kind: 'flat' };
  const axis: RidgeAxis = previous && previous.kind !== 'flat' ? previous.ridgeAxis : (room.length >= room.width ? 'length' : 'width');
  const { across } = spans(room, axis);
  const room_ = maxHeight - room.height;
  if (shape === 'gable') {
    const rise = Math.min(room_, previous && previous.kind !== 'flat' ? previous.rise : riseForPitch(6, across / 2));
    return { kind: 'gable', ridgeAxis: axis, ridgeAt: 0.5, rise: Math.max(0.25, rise) };
  }
  if (shape === 'vaulted') {
    // Half the span flat in the middle; the slopes at 6/12 over the quarter each side.
    const rise = Math.min(room_, previous && previous.kind !== 'flat' ? previous.rise : riseForPitch(6, across / 4));
    return { kind: 'vaulted', ridgeAxis: axis, flat: 0.5, rise: Math.max(0.25, rise) };
  }
  const rise = Math.min(room_, previous && previous.kind !== 'flat' ? previous.rise : riseForPitch(3, across));
  return { kind: 'shed', ridgeAxis: axis, highSide: previous?.kind === 'shed' ? previous.highSide : 'end', rise: Math.max(0.25, rise) };
}

/** Snap a rise to the nearest standard pitch over any of the `runs`, when it is close. */
function snapRise(rise: number, runs: number[], tolerance: number): number {
  let best = rise;
  let gap = tolerance;
  for (const run of runs) {
    for (const p of STANDARD_PITCHES) {
      const r = riseForPitch(p, run);
      if (Math.abs(r - rise) < gap) { gap = Math.abs(r - rise); best = r; }
    }
  }
  return best;
}

const round = (v: number, step: number) => Math.round(v / step) * step;

/** One of the shape tiles, drawn as a room's section. */
const ShapeIcon: React.FC<{ shape: CeilingShape; active: boolean; scale?: number }> = ({ shape, active, scale = 1 }) => {
  const stroke = active ? ACCENT : INK;
  const top = shape === 'flat' ? 'M6,16 L42,16' : shape === 'shed' ? 'M6,20 L42,8' : shape === 'vaulted' ? 'M6,20 L16,8 L32,8 L42,20' : 'M6,20 L24,6 L42,20';
  const wallTopL = shape === 'flat' ? 16 : 20;
  const wallTopR = shape === 'flat' ? 16 : shape === 'shed' ? 8 : 20;
  return (
    <svg width={48 * scale} height={30 * scale} viewBox="0 0 48 30" aria-hidden="true">
      <path d={`M6,28 L6,${wallTopL} ${top.slice(1)} L42,28`} fill={active ? 'rgba(254,177,0,0.14)' : 'rgba(238,241,246,0.06)'} stroke={stroke} strokeWidth={1.6} strokeLinejoin="round" />
      <path d={`M6,${wallTopL} L42,${wallTopR}`} stroke={stroke} strokeWidth={0.8} strokeDasharray="2 2" opacity={shape === 'flat' ? 0 : 0.6} />
      <path d="M3,28 L45,28" stroke={stroke} strokeWidth={1} opacity={0.5} />
    </svg>
  );
};

/** A ridge seen from above: the room's footprint with the line the slope turns on. */
const RidgeIcon: React.FC<{ axis: RidgeAxis; active: boolean }> = ({ axis, active }) => {
  const stroke = active ? '#1a1a1e' : INK;
  return (
    <svg width={22} height={16} viewBox="0 0 22 16" aria-hidden="true">
      <rect x={1.5} y={1.5} width={19} height={13} rx={1.5} fill="none" stroke={stroke} strokeWidth={1.3} />
      {axis === 'length' ? <path d="M4,8 L18,8" stroke={stroke} strokeWidth={2} /> : <path d="M11,4 L11,12" stroke={stroke} strokeWidth={2} />}
    </svg>
  );
};

export const CeilingEditor: React.FC<CeilingEditorProps> = ({ room, count, maxHeight, onChange, others, onCopyTo, variant = 'panel' }) => {
  const wizard = variant === 'wizard';
  const profile = room.ceilingProfile ?? { kind: 'flat' as const };
  const shape = shapeOf(profile);
  const geom = useMemo(() => ceilingGeometry(room), [room]);
  const axis: RidgeAxis = profile.kind === 'flat' ? (room.length >= room.width ? 'length' : 'width') : profile.ridgeAxis;
  const { across, along } = spans(room, axis);
  const maxRise = Math.max(0, maxHeight - room.height);

  // The high point as a fraction across the section (0 = the near/left wall);
  // for a vault, its near shoulder (where the slope meets the flat).
  const peakAt = profile.kind === 'gable' ? profile.ridgeAt
    : profile.kind === 'shed' ? (profile.highSide === 'start' ? 0 : 1)
      : profile.kind === 'vaulted' ? (1 - profile.flat) / 2 : 0.5;
  const rise = profile.kind === 'flat' ? 0 : profile.rise;

  const set = useCallback((next: CeilingProfile) => onChange(next), [onChange]);
  const setRise = (r: number) => {
    if (profile.kind === 'flat') return;
    const clamped = Math.max(0.25, Math.min(maxRise, r));
    if (Math.abs(clamped - profile.rise) >= HALF_INCH_FT / 2) set({ ...profile, rise: clamped });
  };

  // ---- the cross-section drawing ----
  const W = wizard ? 300 : 222;
  const H = wizard ? 186 : 150;
  const pad = 14;
  const svgRef = useRef<SVGSVGElement>(null);
  // While the high point is dragged the drawing keeps the scale it had when
  // the drag began (otherwise the room refits under the pointer and runs away),
  // and the change is measured from where the drag started.
  const dragRef = useRef<{ scale: number; x0: number; eaveY: number; px: number; py: number; rise: number; at: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  // Fit the section (across × peak height) into the box; the walls' height is to the same scale.
  const peak = room.height + Math.max(rise, 0.5);
  const fitScale = Math.min((W - 2 * pad) / across, (H - pad - 34) / peak);
  const scale = dragRef.current?.scale ?? fitScale;
  const x0 = dragRef.current?.x0 ?? (W - across * scale) / 2;
  const floorY = H - 26;
  const yOf = (h: number) => floorY - h * scale;
  const xOf = (f: number) => x0 + f * across * scale;
  const eaveY = yOf(room.height);
  const peakX = xOf(peakAt);
  const peakY = yOf(room.height + rise);

  const onPointerDown = (e: React.PointerEvent) => {
    if (profile.kind === 'flat') return;
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    dragRef.current = { scale, x0, eaveY, px: e.clientX, py: e.clientY, rise: profile.rise, at: peakAt };
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || profile.kind === 'flat') return;
    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    let r = Math.max(0.25, Math.min(maxRise, d.rise - dy / d.scale));
    if (profile.kind === 'gable') {
      // Off centre by dragging sideways; snaps to the middle (and quarters).
      let at = Math.max(0.1, Math.min(0.9, d.at + dx / (across * d.scale)));
      for (const snap of [0.5, 0.25, 0.75]) if (Math.abs(at - snap) < 0.04) at = snap;
      r = snapRise(r, [at * across, (1 - at) * across], 10 / d.scale);
      const next = { ...profile, ridgeAt: round(at, 0.01), rise: round(r, HALF_INCH_FT) };
      if (next.ridgeAt !== profile.ridgeAt || Math.abs(next.rise - profile.rise) >= HALF_INCH_FT / 2) set(next);
    } else if (profile.kind === 'vaulted') {
      // The shoulder: sideways widens or narrows the flat centre (kept centred); snaps to a half and thirds.
      let at = Math.max(0.05, Math.min(0.45, d.at + dx / (across * d.scale)));
      for (const snap of [0.25, 1 / 3, 1 / 6]) if (Math.abs(at - snap) < 0.03) at = snap;
      r = snapRise(r, [at * across], 10 / d.scale);
      const next = { ...profile, flat: round(1 - 2 * at, 0.01), rise: round(r, HALF_INCH_FT) };
      if (Math.abs(next.flat - profile.flat) > 1e-9 || Math.abs(next.rise - profile.rise) >= HALF_INCH_FT / 2) set(next);
    } else {
      // A sloped ceiling: drag toward the other wall to make that the high one.
      const fx = d.at + dx / (across * d.scale);
      const highSide = fx < 0.5 ? 'start' : 'end';
      r = snapRise(r, [across], 10 / d.scale);
      const next = { ...profile, highSide, rise: round(r, HALF_INCH_FT) } as CeilingProfile & { kind: 'shed' };
      if (next.highSide !== profile.highSide || Math.abs(next.rise - profile.rise) >= HALF_INCH_FT / 2) set(next);
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
    try { (e.target as Element).releasePointerCapture(e.pointerId); } catch { /* already released */ }
  };

  // Words for what is there.
  const pitches = geom.planes.filter((p) => p.rise > 1e-6).map((p) => p.pitch);
  const pitchText = (p: number) => `${Math.round(p * 10) / 10}/12`;
  const summary = profile.kind === 'flat'
    ? C.flatSummary(formatFeetInches(room.height))
    : pitches.length === 1 || (pitches.length === 2 && Math.abs(pitches[0] - pitches[1]) < 0.05)
      ? C.summary(formatFeetInches(geom.peak), pitchText(pitches[0]), C.pitchWords[pitchWord(pitches[0])])
      : C.summaryTwo(formatFeetInches(geom.peak), pitchText(pitches[0]), pitchText(pitches[1]));

  // The pitch select: the standard pitches over the (symmetric) run, or "custom".
  const run = profile.kind === 'gable' ? across * Math.min(profile.ridgeAt, 1 - profile.ridgeAt)
    : profile.kind === 'vaulted' ? (across * (1 - profile.flat)) / 2 : across;
  const currentPitch = profile.kind === 'flat' ? 0 : pitchForRise(profile.rise, run);
  const standard = STANDARD_PITCHES.find((p) => Math.abs(p - currentPitch) < 0.05);

  const tile = (active: boolean): React.CSSProperties => ({
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '6px 2px 5px',
    borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: active ? ACCENT : INK,
    background: active ? 'rgba(254,177,0,0.12)' : 'rgba(255,255,255,0.06)', border: `1px solid ${active ? ACCENT : 'rgba(255,255,255,0.18)'}`,
  });
  const small: React.CSSProperties = { fontSize: 10, fontWeight: 400, color: SOFT };
  const toggle = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '4px 6px', borderRadius: 6, fontSize: 11, cursor: 'pointer', textAlign: 'center',
    color: active ? '#1a1a1e' : INK, background: active ? ACCENT : 'rgba(255,255,255,0.08)', border: `1px solid ${active ? ACCENT : 'rgba(255,255,255,0.2)'}`,
  });

  const shapeTiles = (
    <div role="radiogroup" aria-label={C.title} style={{ display: 'flex', gap: wizard ? 10 : 6 }}>
      {(['flat', 'shed', 'gable', 'vaulted'] as CeilingShape[]).map((s) => (
        <button
          key={s} type="button" role="radio" aria-checked={shape === s} aria-label={C.shapes[s].label}
          onClick={() => { if (s !== shape) set(startingProfile(room, s, profile, maxHeight)); }}
          style={wizard ? { ...tile(shape === s), padding: '12px 4px 10px', gap: 4, fontSize: 13, borderRadius: 10 } : tile(shape === s)}
        >
          <ShapeIcon shape={s} active={shape === s} scale={wizard ? 1.5 : 1} />
          <span>{C.shapes[s].label}</span>
          <span style={wizard ? { ...small, fontSize: 11, lineHeight: 1.25 } : small}>{wizard ? C.shapes[s].plain : C.shapes[s].builder}</span>
        </button>
      ))}
    </div>
  );

  if (wizard) {
    const sloped = profile.kind !== 'flat';
    return (
      <div>
        {shapeTiles}
        {!sloped && <p className="cw-ceiling-flat">{C.flatWizard(formatFeetInches(room.height))}</p>}
        {sloped && (
          <div className="cw-ceiling-grid">
            {renderSection()}
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#fff', lineHeight: 1.35 }}>{summary}</p>
              <p className="cw-label">{C.steepness}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                {STEEPNESS.map((p) => {
                  const r = riseForPitch(p, run);
                  const fits = r <= maxRise + 1e-6;
                  const active = Math.abs(currentPitch - p) < 0.05;
                  return (
                    <button
                      key={p} type="button" aria-pressed={active} disabled={!fits}
                      title={fits ? `${p}/12` : C.tooSteep}
                      onClick={() => setRise(r)}
                      style={{ ...toggle(active), padding: '6px 2px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, opacity: fits ? 1 : 0.35, cursor: fits ? 'pointer' : 'not-allowed' }}
                    >
                      <span style={{ fontWeight: 700, fontSize: 12 }}>{C.steepWords[p]}</span>
                      <span style={{ fontSize: 10, opacity: 0.8 }}>{p}/12</span>
                    </button>
                  );
                })}
              </div>
              <p className="cw-label" style={{ marginTop: 12 }}>{C.runs}</p>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['length', 'width'] as RidgeAxis[]).map((a) => (
                  <button
                    key={a} type="button" aria-pressed={axis === a} aria-label={C.axis[a]}
                    onClick={() => { if (a !== axis) set({ ...profile, ridgeAxis: a, rise: Math.min(profile.rise, maxRise) }); }}
                    style={{ ...toggle(axis === a), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 6px' }}
                  >
                    <RidgeIcon axis={a} active={axis === a} /> {C.axis[a]}
                  </button>
                ))}
              </div>
              {profile.kind === 'gable' && Math.abs(profile.ridgeAt - 0.5) > 1e-6 && (
                <button type="button" onClick={() => set({ ...profile, ridgeAt: 0.5 })} style={{ ...toggle(false), width: '100%', marginTop: 8 }}>{C.centre}</button>
              )}
              <details className="cw-details">
                <summary>{C.exactNumbers}</summary>
                {renderNumbers()}
              </details>
            </div>
          </div>
        )}
        {sloped && <p style={{ margin: '10px 0 0', color: '#a9abb3', lineHeight: 1.4 }}>{C.wallsStay}</p>}
        {count > 1 && <p style={{ margin: '6px 0 0', color: '#a9abb3' }}>{C.appliesToAll(count)}</p>}
        {renderCopyTo()}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{C.title}</p>
      {shapeTiles}
      <p style={{ margin: '8px 0 0', color: '#c9cbd3', lineHeight: 1.4 }}>{summary}</p>
      {count > 1 && <p style={{ margin: '4px 0 0', color: '#a9abb3' }}>{C.appliesToAll(count)}</p>}

      {profile.kind !== 'flat' && (
        <>
          {/* Which way the slope runs: along the room's length or its width. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
            <span style={{ fontWeight: 700, flex: '0 0 auto' }}>{C.runs}</span>
            {(['length', 'width'] as RidgeAxis[]).map((a) => (
              <button
                key={a} type="button" aria-pressed={axis === a} aria-label={C.axis[a]}
                onClick={() => { if (a !== axis) set({ ...profile, ridgeAxis: a, rise: Math.min(profile.rise, maxRise) }); }}
                style={toggle(axis === a)}
              >
                {C.axis[a]}
              </button>
            ))}
          </div>

          {renderSection()}
          {renderNumbers()}
          {profile.kind === 'gable' && Math.abs(profile.ridgeAt - 0.5) > 1e-6 && (
            <button type="button" onClick={() => set({ ...profile, ridgeAt: 0.5 })} style={{ ...toggle(false), width: '100%', marginTop: 8 }}>{C.centre}</button>
          )}
          <p style={{ margin: '8px 0 0', color: '#a9abb3', lineHeight: 1.4 }}>{C.wallsStay}</p>
        </>
      )}
      {renderCopyTo()}
    </div>
  );

  /** Give the other rooms this ceiling (one room selected). */
  function renderCopyTo() {
    if (!onCopyTo || !others || others.length === 0) return null;
    return (
      <div style={{ marginTop: 10 }}>
        <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{C.copyTo}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {others.length > 1 && (
            <button type="button" onClick={() => onCopyTo(others.map((o) => o.id))} style={{ ...toggle(false), flex: '0 0 auto' }}>{C.copyToAll}</button>
          )}
          {others.map((o) => (
            <button key={o.id} type="button" title={C.copyToTitle(o.name)} onClick={() => onCopyTo([o.id])} style={{ ...toggle(false), flex: '0 0 auto' }}>{o.name}</button>
          ))}
        </div>
      </div>
    );
  }

  /** The cross-section: drag the high point. */
  function renderSection() {
    if (profile.kind === 'flat') return null;
    return (
          <svg
            ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`}
            role="img" aria-label={C.sectionLabel}
            style={{ display: 'block', marginTop: 8, borderRadius: 8, background: 'rgba(21,62,110,0.55)', border: '1px solid rgba(255,255,255,0.12)', touchAction: 'none', cursor: dragging ? 'grabbing' : 'default' }}
            onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
          >
            {/* Floor and walls (to the wall height), then the ceiling line(s). */}
            <line x1={x0 - 6} y1={floorY} x2={x0 + across * scale + 6} y2={floorY} stroke={SOFT} strokeWidth={1} />
            <path
              d={`M${x0},${floorY} L${x0},${profile.kind === 'shed' && profile.highSide === 'start' ? peakY : eaveY} ${profile.kind === 'gable' ? `L${peakX},${peakY}` : profile.kind === 'vaulted' ? `L${peakX},${peakY} L${xOf(1 - peakAt)},${peakY}` : ''} L${xOf(1)},${profile.kind === 'shed' && profile.highSide === 'end' ? peakY : eaveY} L${xOf(1)},${floorY} Z`}
              fill="rgba(238,241,246,0.08)" stroke={INK} strokeWidth={1.5} strokeLinejoin="round"
            />
            {/* The wall height, as a faint line across. */}
            <line x1={x0} y1={eaveY} x2={xOf(1)} y2={eaveY} stroke={SOFT} strokeWidth={0.8} strokeDasharray="3 3" />
            <text x={x0 - 4} y={eaveY + 3} textAnchor="end" fontSize={9} fill={SOFT}>{formatFeetInches(room.height)}</text>
            {/* Across dimension. */}
            <text x={(x0 + xOf(1)) / 2} y={floorY + 12} textAnchor="middle" fontSize={9} fill={SOFT}>{C.across(formatFeetInches(across), formatFeetInches(along))}</text>
            {/* The peak: its height, and the handle. */}
            <text x={Math.min(Math.max(peakX, x0 + 28), xOf(1) - 28)} y={Math.max(10, peakY - 10)} textAnchor="middle" fontSize={10} fontWeight={700} fill={ACCENT}>{formatFeetInches(geom.peak)}</text>
            <circle
              cx={peakX} cy={peakY} r={7} fill={ACCENT} stroke="#1a1a1e" strokeWidth={1.5}
              style={{ cursor: dragging ? 'grabbing' : 'grab' }}
              onPointerDown={onPointerDown}
              role="slider" aria-label={C.handle} aria-valuenow={Math.round(geom.peak * 12)} aria-valuemin={Math.round(room.height * 12)} aria-valuemax={Math.round(maxHeight * 12)} tabIndex={0}
              onKeyDown={(e) => {
                const step = e.shiftKey ? 1 : e.ctrlKey ? 1 / 12 : 0.5;
                if (e.key === 'ArrowUp') { e.preventDefault(); setRise(profile.rise + step); }
                if (e.key === 'ArrowDown') { e.preventDefault(); setRise(profile.rise - step); }
                if (profile.kind === 'gable' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                  e.preventDefault();
                  const at = Math.max(0.1, Math.min(0.9, profile.ridgeAt + (e.key === 'ArrowLeft' ? -0.05 : 0.05)));
                  set({ ...profile, ridgeAt: round(at, 0.01) });
                }
                if (profile.kind === 'vaulted' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                  e.preventDefault();
                  const flat = Math.max(0.1, Math.min(0.9, profile.flat + (e.key === 'ArrowLeft' ? 0.05 : -0.05)));
                  set({ ...profile, flat: round(flat, 0.01) });
                }
              }}
            />
            <text x={W / 2} y={H - 4} textAnchor="middle" fontSize={9} fill={SOFT}>{profile.kind === 'gable' ? C.dragHintGable : profile.kind === 'vaulted' ? C.dragHintVaulted : C.dragHintShed}</text>
          </svg>
    );
  }

  /** The builder's numbers, all linked. */
  function renderNumbers() {
    if (profile.kind === 'flat') return null;
    return (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
            <label style={{ flex: 1, fontWeight: 700 }}>
              {C.pitch}
              <select
                value={standard ?? 'custom'} aria-label={C.pitch}
                onChange={(e) => { if (e.target.value !== 'custom') setRise(riseForPitch(Number(e.target.value), run)); }}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '5px 6px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12 }}
              >
                {!standard && <option value="custom">{C.customPitch(pitchText(currentPitch))}</option>}
                {STANDARD_PITCHES.map((p) => <option key={p} value={p} style={{ color: '#000' }}>{`${p}/12 · ${C.pitchWords[pitchWord(p)]}`}</option>)}
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <label style={{ flex: 1, fontWeight: 700 }}>
              {C.rise}
              <span style={{ display: 'block', marginTop: 4 }}>
                <DimensionInput value={rise} mode="ftin" min={0.25} max={maxRise} ariaLabel={C.rise} onChange={(v) => setRise(v)} />
              </span>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <label style={{ flex: 1, fontWeight: 700 }}>
              {C.peak}
              <span style={{ display: 'block', marginTop: 4 }}>
                <DimensionInput value={room.height + rise} mode="ftin" min={room.height + 0.25} max={maxHeight} ariaLabel={C.peak} onChange={(v) => setRise(v - room.height)} />
              </span>
            </label>
          </div>
          {profile.kind === 'vaulted' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <label style={{ flex: 1, fontWeight: 700 }}>
                {C.flatWidth}
                <span style={{ display: 'block', marginTop: 4 }}>
                  <DimensionInput
                    value={across * profile.flat} mode="ftin" min={across * 0.1} max={across * 0.9} ariaLabel={C.flatWidth}
                    onChange={(v) => { const flat = Math.max(0.1, Math.min(0.9, v / across)); if (Math.abs(flat - profile.flat) > 1e-4) set({ ...profile, flat: round(flat, 0.001) }); }}
                  />
                </span>
              </label>
            </div>
          )}
        </>
    );
  }
};

export default CeilingEditor;
